import React, { Component } from 'react';

import { bool, shape, string } from 'prop-types';
import uuid from 'uuid/v4';

import { ChecklistAPI } from '@philips-emr/tws-components-api';
import { Density } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { arrayOf, eventListener, oneOf } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isDefined, isEnumValid, isFunction, mobile, shallowCompare } from '@philips-emr/tws-utils';

import { Checkbox } from '..';
import Loader from '../loader/loader';
import NativeSelect from '../native-select/native-select';

import './checklist.css';

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: ChecklistAPI })
class Checklist extends Component {

  static propTypes = {
    className: string,
    data: arrayOf(shape({ key: string, label: string, value: bool })).notEnumerable(),
    density: oneOf(enumValues(Density)),
    disabledKeys: arrayOf(string).notEnumerable(),
    enabled: bool,
    loading: bool,
    name: string,
    nativeOnMobile: bool,
    onChange: eventListener,
    readOnly: bool,
    showCheckAll: bool,
    uuid: string,
    value: arrayOf(bool).notEnumerable(),
  };

  static defaultProps = {
    data: [],
    density: Density.REGULAR,
    disabledKeys: [],
    enabled: true,
    loading: false,
    nativeOnMobile: false,
    showCheckAll: true,
    value: [],
  };

  shouldComponentUpdate(nextProps) {
    return shallowCompare(this.props, nextProps, [
      'className',
      'data',
      'density',
      'disabledKeys',
      'enabled',
      'loading',
      'name',
      'nativeOnMobile',
      'readOnly',
      'showCheckAll',
      'uuid',
      'value',
    ]);
  }

  onChange = (item, checked) => {
    const { value: oldValue, onChange } = this.props;
    const isAlreadyChecked = oldValue.some(({ key }) => key === item.key);

    if (checked && isAlreadyChecked) return;

    const value = checked
      ? [...oldValue, item]
      : oldValue.filter(({ key }) => key !== item.key);

    isFunction(onChange) && onChange({ value, oldValue });
  };

  onChangeNative = ({ value }) => {
    const { value: oldValue, onChange } = this.props;

    isFunction(onChange) && onChange({ value: value ? [value] : undefined, oldValue });
  };

  onChangeCheckAll = ({ value: checked }) => {
    const { value: oldValue, data, onChange } = this.props;

    isFunction(onChange) && onChange({ value: checked ? data : [], oldValue });
  };

  isAllChecked() {
    const { data } = this.props;
    return data.every(this.isChecked);
  }

  isChecked = ({ key }) => {
    const { value } = this.props;
    return Array.isArray(value) && value.some(item => item.key === key);
  };

  renderCheckAll() {
    const { enabled, readOnly, showCheckAll } = this.props;

    if (readOnly || !showCheckAll) {
      return null;
    }

    return (
      <li className="c-checklist__item c-checklist__check-all">
        <Checkbox name={uuid()} label="tws.checkbox.check-all" value={this.isAllChecked()} enabled={enabled} readOnly={readOnly} onChange={this.onChangeCheckAll} />
      </li>
    );
  }

  renderItems() {
    const { data, disabledKeys, enabled, name, readOnly } = this.props;

    if (!Array.isArray(data)) {
      return null;
    }

    const validData = data.filter((item) => isDefined(item.key));

    if (!validData.length) {
      return <li className="c-checklist__item" data-testid="checklist-no-options">{msg('tws.checkbox.no-options')}</li>;
    }

    return (
      <React.Fragment>
        {validData.map((item) => {
          const checkboxName = `${name}-${item.key}`;
          const itemEnabled = !disabledKeys.includes(item.key);

          return (
            <li className="c-checklist__item" data-testid="checklist-option" key={item.key}>
              <Checkbox
                enabled={enabled && itemEnabled}
                label={msg(item.label)}
                name={checkboxName}
                onChange={({ value: checked }) => this.onChange(item, checked)}
                readOnly={readOnly}
                value={this.isChecked(item)}
              />
            </li>
          );
        })}

        {this.renderCheckAll()}
      </React.Fragment>
    );
  }

  renderChecklist() {
    const { loading, density, className } = this.props;

    const clClassName = classNames({
      'c-checklist': true,
      [`c-checklist--${density}`]: isEnumValid(Density, density),
      [className]: Boolean(className),
    });

    return (
      <div className={clClassName}>
        <ul className="c-checklist__items">
          {loading
            ? <li><Loader/></li>
            : this.renderItems()}
        </ul>
      </div>
    );
  }

  renderNative() {
    const { data, value } = this.props;

    return <NativeSelect value={value[0]} data={data} onChange={this.onChangeNative} />;
  }

  render() {
    const isNative = mobile.isMobile() && this.props.nativeOnMobile;

    return isNative
      ? this.renderNative()
      : this.renderChecklist();
  }
}

export default Checklist;

